import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import type { GlobalSharedContent } from 'src/models/global/global-shared-content';
import type { CmsFileBase, CmsGlobalFileNode } from 'src/helpers/cms-file-helper';
import { findCmsFileNodeForBrand, parseRelativeDirectory } from 'src/helpers/cms-file-helper';
import { SiteContext } from 'src/context/site-context';
import type { FooterContent, NewFooterContent } from 'src/models/cms/footer-content';
import { useMemo } from 'react';
import type { LanguageIsoCode } from 'src/enums/language';

const globalCmsContentQuery = graphql`
  fragment _GlobalSharedContent on GlobalJson {
    mobileApp {
      title
      description
      image {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    nextStep {
      title
      description
      button {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      secondaryButton {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
    }
    atGlobalUnit {
      showBlock
      title
      description
      button {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      imagesWithLink {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        link
        title
        description
      }
    }
    navigationPagesToDisplay {
      mainPages {
        displayText
        link
      }
      secondaryPages {
        displayText
        link
      }
    }
    unitLocation {
      addressLine1
      addressLine2
      mapUrl
    }
    enquireNowButton {
      enquireNowButtonText
      enquireNowButtonLink
      goToGlobal
    }
    linkDropdownCta {
      enabled
      text
      dropdownOptions {
        displayText
        link
      }
    }
  }

  fragment _GlobalFooterContent on GlobalJson {
    navMenuSections {
      title
      links {
        type
        text
        url
        openInNewTab
      }
    }
    footerImagesSections {
      title
      images {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        size
        link
        setToExpire
        expiryDate
      }
    }
    copyrightInformation
    vatInformation
    registeredCompanyInformation
    imagesDisclaimer
  }

  query GlobalCmsContentQuery {
    sharedContent: allFile(filter: { base: { eq: "global-shared.json" } }) {
      nodes {
        relativeDirectory
        childGlobalJson {
          ..._GlobalSharedContent
        }
      }
    }
    footerContent: allFile(filter: { base: { eq: "footer.json" } }) {
      nodes {
        relativeDirectory
        childGlobalJson {
          ..._GlobalFooterContent
        }
      }
    }
    newFooterContent: allFile(filter: { base: { eq: "footer-redesign.json" } }) {
      nodes {
        relativeDirectory
        childGlobalJson {
          ..._GlobalFooterRedesignFragment
        }
      }
    }
    headerContent: allFile(filter: { base: { eq: "header.json" } }) {
      nodes {
        relativeDirectory
        childGlobalJson {
          ..._GlobalHeaderContent
        }
      }
    }
    appPromoTemplateContent: allFile(
      filter: { relativeDirectory: { eq: "cross-club/template-app-promo" } }
    ) {
      nodes {
        relativeDirectory
        childTemplateAppPromoJson {
          en {
            ..._TemplateAppPromo
          }
          ca {
            ..._TemplateAppPromo
          }
          de {
            ..._TemplateAppPromo
          }
          es {
            ..._TemplateAppPromo
          }
          fr {
            ..._TemplateAppPromo
          }
          it {
            ..._TemplateAppPromo
          }
          nl {
            ..._TemplateAppPromo
          }
        }
      }
    }
    blogTemplateContent: allFile(
      filter: { relativeDirectory: { eq: "cross-club/template-blog" } }
    ) {
      nodes {
        relativeDirectory
        childTemplateBlogJson {
          en {
            ..._TemplateBlog
          }
          ca {
            ..._TemplateBlog
          }
          de {
            ..._TemplateBlog
          }
          es {
            ..._TemplateBlog
          }
          fr {
            ..._TemplateBlog
          }
          it {
            ..._TemplateBlog
          }
          nl {
            ..._TemplateBlog
          }
        }
      }
    }
    membershipTemplateContent: allFile(
      filter: { relativeDirectory: { eq: "cross-club/template-membership-content" } }
    ) {
      nodes {
        relativeDirectory
        childTemplateMembershipContentJson {
          en {
            ..._TemplateMembershipContent
          }
          ca {
            ..._TemplateMembershipContent
          }
          de {
            ..._TemplateMembershipContent
          }
          es {
            ..._TemplateMembershipContent
          }
          fr {
            ..._TemplateMembershipContent
          }
          it {
            ..._TemplateMembershipContent
          }
          nl {
            ..._TemplateMembershipContent
          }
        }
      }
    }
    whyJoinListTemplateContent: allFile(
      filter: { relativeDirectory: { eq: "cross-club/template-why-join-list" } }
    ) {
      nodes {
        relativeDirectory
        childTemplateWhyJoinListJson {
          en {
            ..._TemplateWhyJoinList
          }
          ca {
            ..._TemplateWhyJoinList
          }
          de {
            ..._TemplateWhyJoinList
          }
          es {
            ..._TemplateWhyJoinList
          }
          fr {
            ..._TemplateWhyJoinList
          }
          it {
            ..._TemplateWhyJoinList
          }
          nl {
            ..._TemplateWhyJoinList
          }
        }
      }
    }
  }
`;

interface WrappedGlobalCmsRecord<T> {
  sharedContent: T;
  headerContent: T;
  footerContent: T;
  newFooterContent: T;
  appPromoTemplateContent: T;
  blogTemplateContent: T;
  membershipTemplateContent: T;
  whyJoinListTemplateContent: T;
}

export type GlobalHeaderContent = {
  headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
  headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
  headerPromoRibbon?: Queries.HeaderPromoRibbon;
  headerRecentlyViewedClubRibbon?: Queries.GlobalHeaderRecentlyViewedClubRibbon;
  headerButton?: Queries.RedesignButton;
  headerOtherText?: Queries.HeaderOtherText;
  searchSettings: Queries.HeaderSearchSettings;
  linkDropdownCta?: Queries.LinkDropdownCta;
};

export interface GlobalCmsRecord {
  sharedContent: GlobalSharedContent;
  headerContent: GlobalHeaderContent;
  footerContent: FooterContent;
  newFooterContent: NewFooterContent;
  appPromoTemplateContent: Record<LanguageIsoCode, Queries.TemplateAppPromo>[];
  blogTemplateContent: Record<LanguageIsoCode, Queries.TemplateBlog>[];
  membershipTemplateContent: Record<LanguageIsoCode, Queries.TemplateMembershipContent>[];
  whyJoinListTemplateContent: Record<LanguageIsoCode, Queries.TemplateWhyJoinList>[];
}

type CmsTemplateFileNode<T> = CmsFileBase & {
  childTemplateAppPromoJson?: T;
  childTemplateBlogJson?: T;
  childTemplateMembershipContentJson?: T;
  childTemplateWhyJoinListJson?: T;
};

type CmsGlobalRecordNode<T> = CmsGlobalFileNode<T> | CmsTemplateFileNode<T>;

const keysWithMultipleNodes: (keyof GlobalCmsRecord)[] = [
  'appPromoTemplateContent',
  'blogTemplateContent',
  'membershipTemplateContent',
  'whyJoinListTemplateContent',
];

const childJsonKeys = [
  'childGlobalJson',
  'childTemplateAppPromoJson',
  'childTemplateBlogJson',
  'childTemplateMembershipContentJson',
  'childTemplateWhyJoinListJson',
] as const;

const extractChildFromNode = <T,>(node: CmsGlobalRecordNode<T>) => {
  const childKey = childJsonKeys.find((key) => {
    if (key in node) {
      return true;
    }
  })!;
  // @ts-ignore
  return node[childKey];
};

export const GlobalCmsProvider = ({
  children,
}: {
  children: (cmsContent: GlobalCmsRecord) => React.ReactNode;
}) => {
  const { language, buildType } = React.useContext(SiteContext);

  const cms =
    useStaticQuery<WrappedGlobalCmsRecord<{ nodes: CmsGlobalRecordNode<any>[] }>>(
      globalCmsContentQuery
    );

  const cmsRecord = useMemo(() => {
    (Object.keys(cms) as (keyof typeof cms)[]).forEach((key) => {
      cms[key].nodes.forEach(parseRelativeDirectory);
    });

    return (Object.keys(cms) as (keyof typeof cms)[]).reduce((record, key) => {
      if (keysWithMultipleNodes.includes(key)) {
        // @ts-ignore
        record[key] = cms[key].nodes.map(extractChildFromNode);
        return record;
      }

      const cmsNode = findCmsFileNodeForBrand(cms[key].nodes, language, buildType);

      record[key] = cmsNode ? extractChildFromNode(cmsNode) : null;

      return record;
    }, {} as GlobalCmsRecord);
  }, [buildType, cms, language]);

  return children(cmsRecord);
};
